import { IMMICH_BASE_URL } from "../config/immichConfig";

const IMMICH_CREATE_PERSON_URL = `${IMMICH_BASE_URL}/people/createPerson`;
const IMMICH_CREATE_PERSON_FALLBACK_URL = `${IMMICH_BASE_URL}/people`;
const IMMICH_API_KEY = (process.env.REACT_APP_IMMICH_API_KEY || "").trim();
const IMMICH_UPLOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_IMMICH_UPLOAD_TIMEOUT_MS || 180000,
);

const IMMICH_PERSON_ROLE_SUFFIX = {
  student: "",
  father: "_father",
  mother: "_mother",
};

function emitImmichLog(onLog, level, role, message, details = null) {
  if (typeof onLog !== "function") {
    return;
  }

  onLog({
    level,
    role,
    message,
    details,
    timestamp: new Date().toISOString(),
  });
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 45000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function extractResponsePayload(response) {
  const textBody = await response.text();
  let jsonBody = null;

  try {
    jsonBody = textBody ? JSON.parse(textBody) : null;
  } catch {
    jsonBody = null;
  }

  return { textBody, jsonBody };
}

function parseImmichErrorMessage(status, textBody, defaultMessage) {
  const normalizedText = String(textBody || "").trim();

  if (!normalizedText) {
    return `${defaultMessage} (${status})`;
  }

  const shortText =
    normalizedText.length > 220
      ? `${normalizedText.slice(0, 220)}...`
      : normalizedText;

  return `${defaultMessage} (${status}): ${shortText}`;
}

function getImmichNetworkError(error) {
  if (error?.name === "AbortError") {
    return `Request timed out after ${IMMICH_UPLOAD_TIMEOUT_MS}ms`;
  }

  const raw = String(error?.message || "");
  if (raw.toLowerCase().includes("failed to fetch")) {
    return "Network/CORS/Proxy issue (Failed to fetch)";
  }

  return raw || "Unknown network error";
}

export function buildImmichPersonName(uid, role = "student", displayName = "") {
  const normalizedDisplayName = String(displayName || "").trim();
  if (normalizedDisplayName) {
    return normalizedDisplayName;
  }

  const uidValue = String(uid || "").trim();
  if (!uidValue) {
    return "";
  }

  const normalizedRole = String(role || "student").toLowerCase().trim();
  const suffix = IMMICH_PERSON_ROLE_SUFFIX[normalizedRole] ?? "";

  return `${uidValue}${suffix}`;
}

async function findImmichPersonByName(personName, onLog = null, role = "student") {
  const searchUrl = `${IMMICH_BASE_URL}/search/person?${new URLSearchParams({
    name: personName,
    withHidden: "true",
  }).toString()}`;

  emitImmichLog(onLog, "info", role, "Searching for existing person", {
    endpoint: searchUrl,
    personName,
  });

  const response = await fetchWithTimeout(
    searchUrl,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "x-api-key": IMMICH_API_KEY,
      },
    },
    IMMICH_UPLOAD_TIMEOUT_MS,
  );

  if (!response.ok) {
    return null;
  }

  const { jsonBody } = await extractResponsePayload(response);
  const people = Array.isArray(jsonBody) ? jsonBody : [];
  const lowerName = personName.toLowerCase();

  return (
    people.find((person) => String(person?.name || "").toLowerCase() === lowerName) ||
    people.find((person) => String(person?.name || "").toLowerCase().includes(lowerName)) ||
    null
  );
}

async function createImmichFaceForPerson({
  personId,
  assetId,
  imageWidth,
  imageHeight,
  onLog = null,
  role = "student",
  personName = "",
}) {
  const facePayload = {
    assetId,
    personId,
    height: imageHeight,
    imageHeight,
    imageWidth,
    width: imageWidth,
    x: 0,
    y: 0,
  };

  const endpoint = `${IMMICH_BASE_URL}/faces`;

  emitImmichLog(onLog, "info", role, "Preparing createFace request", {
    endpoint,
    personId,
    personName,
    assetId,
    imageWidth,
    imageHeight,
  });

  const response = await fetchWithTimeout(
    endpoint,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-api-key": IMMICH_API_KEY,
      },
      body: JSON.stringify(facePayload),
    },
    IMMICH_UPLOAD_TIMEOUT_MS,
  );

  if (!response.ok) {
    const { textBody } = await extractResponsePayload(response);
    throw new Error(
      parseImmichErrorMessage(response.status, textBody, "Immich createFace failed"),
    );
  }

  emitImmichLog(onLog, "success", role, "Face created successfully", {
    endpoint,
    personId,
    personName,
    assetId,
  });

  return true;
}

export async function syncImmichPersonByUid(uid, role = "student", options = {}) {
  const { onLog, displayName = "", faceAsset = null } = options;
  const uidValue = String(uid || "").trim();
  const personName = buildImmichPersonName(uidValue, role, displayName);
  const roleValue = String(role || "student");

  if (!uidValue) {
    emitImmichLog(onLog, "warning", roleValue, "Skipped sync: missing UID");
    return {
      status: "skipped",
      message: "Missing student UID, skipped Immich people sync.",
    };
  }

  if (!IMMICH_API_KEY) {
    emitImmichLog(
      onLog,
      "warning",
      roleValue,
      "Skipped sync: missing REACT_APP_IMMICH_API_KEY",
    );
    return {
      status: "skipped",
      message: "Missing REACT_APP_IMMICH_API_KEY, skipped Immich people sync.",
    };
  }

  const payload = {
    birthDate: null,
    color: null,
    isFavorite: false,
    isHidden: false,
    name: personName,
  };

  const requestOptions = {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": IMMICH_API_KEY,
    },
    body: JSON.stringify(payload),
  };

  emitImmichLog(onLog, "info", roleValue, "Preparing createPerson request", {
    endpoint: IMMICH_CREATE_PERSON_URL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": IMMICH_API_KEY ? "***configured***" : "***missing***",
    },
    body: payload,
  });

  try {
    let response = await fetchWithTimeout(
      IMMICH_CREATE_PERSON_URL,
      requestOptions,
      IMMICH_UPLOAD_TIMEOUT_MS,
    );
    let usedUrl = IMMICH_CREATE_PERSON_URL;

    emitImmichLog(onLog, "info", roleValue, "createPerson response received", {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
    });

    if (response.status === 404) {
      emitImmichLog(
        onLog,
        "warning",
        roleValue,
        "Primary endpoint returned 404, trying fallback endpoint",
        {
          fallbackEndpoint: IMMICH_CREATE_PERSON_FALLBACK_URL,
        },
      );

      response = await fetchWithTimeout(
        IMMICH_CREATE_PERSON_FALLBACK_URL,
        requestOptions,
        IMMICH_UPLOAD_TIMEOUT_MS,
      );
      usedUrl = IMMICH_CREATE_PERSON_FALLBACK_URL;

      emitImmichLog(
        onLog,
        "info",
        roleValue,
        "Fallback createPerson response received",
        {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        },
      );
    }

    const { textBody, jsonBody } = await extractResponsePayload(response);
    let resolvedPersonId = jsonBody?.id || jsonBody?.person?.id || null;
    let personCreateSkipped = false;

    if (!response.ok) {      const lowerBody = String(textBody || "").toLowerCase();
      const alreadyExists =
        response.status === 409 ||
        lowerBody.includes("already exists") ||
        lowerBody.includes("duplicate");

      if (alreadyExists) {
        const existingPerson = await findImmichPersonByName(personName, onLog, roleValue);
        resolvedPersonId = existingPerson?.id || null;
        personCreateSkipped = true;

        emitImmichLog(
          onLog,
          "warning",
          roleValue,
          `Person already exists: ${personName}`,
          {
            endpoint: usedUrl,
            status: response.status,
            resolvedPersonId,
          },
        );

        if (!resolvedPersonId) {
          return {
            status: "success",
            personId: null,
            personName,
            personCreateSkipped: true,
            personCreateUrl: usedUrl,
            role: roleValue,
          };
        }
      } else {
        throw new Error(
          parseImmichErrorMessage(
            response.status,
            textBody,
            "Immich createPerson failed",
          ),
        );
      }
    }

    emitImmichLog(onLog, "success", roleValue, "Person created successfully", {
      endpoint: usedUrl,
      personName,
      personId: resolvedPersonId,
    });

    let faceCreated = false;
    let faceCreateError = "";

    if (resolvedPersonId && faceAsset?.assetId && faceAsset?.imageWidth && faceAsset?.imageHeight) {
      try {
        faceCreated = await createImmichFaceForPerson({
          personId: resolvedPersonId,
          assetId: faceAsset.assetId,
          imageWidth: faceAsset.imageWidth,
          imageHeight: faceAsset.imageHeight,
          onLog,
          role: roleValue,
          personName,
        });
      } catch (error) {
        faceCreateError = getImmichNetworkError(error) || error.message;
        emitImmichLog(onLog, "warning", roleValue, "Face creation skipped", {
          endpoint: `${IMMICH_BASE_URL}/faces`,
          personId: resolvedPersonId,
          personName,
          message: faceCreateError,
        });
      }
    }

    return {
      status: "success",
      personId: resolvedPersonId,
      personName: jsonBody?.name || jsonBody?.person?.name || personName,
      personCreateSkipped,
      personCreateUrl: usedUrl,
      role: roleValue,
      faceCreated,
      faceCreateError,
    };
  } catch (error) {
    const message = getImmichNetworkError(error) || error.message;

    emitImmichLog(onLog, "error", roleValue, "Person sync failed", {
      endpoint: IMMICH_CREATE_PERSON_URL,
      personName,
      message,
    });

    return {
      status: "failed",
      message,
      role: roleValue,
      personName,
    };
  }
}

